import ko from "knockout";
import _ from "lodash";
import authService from "services/authService";
import loggingService from "services/loggingService";
import participantStatus from "enums/participantStatus";

const PARTICIPANT_STATES = {
    DISCONNECTED: "disconnected",
    HELD: "held",
    CONNECTED: "connected",
    ALERTING: "alerting",
    CONNECTING: "connecting",
    UNKNOWN: "unknown",
};

const CUSTOMER_PARTICIPANTS = ["manual", "inbound", "dialer", "customer"];
const MACHINE_PARTICIPANTS = ["acd", "ivr", "dialer.system"];

const communicationMixin = function (self) {
    self.participantStates = PARTICIPANT_STATES;
    const ownerId = authService.principalId();

    //////////////////////
    // Participant Helpers
    //////////////////////

    const isMachineParticipant = function (participant) {
        if (!participant.purpose) {
            return false;
        }

        return _.indexOf(MACHINE_PARTICIPANTS, participant.purpose()) > -1;
    };

    const isCurrentUserOrMachineParticipant = function (participant) {
        if (!self.participantForCurrentUser()) {
            return false;
        }

        return (
            ((participant.purpose() === "user" ||
                participant.purpose() === "agent") &&
                participant.userId() ===
                    self.participantForCurrentUser().userId()) ||
            isMachineParticipant(participant)
        );
    };

    /**
     * Get the participant object that shares the same user ID as the currently
     * authenticated user.   The list is searched in reverse order as newer participants
     * are added to the end, and we need to be sure to get the most recent instance of the
     * authenticated user.
     * @return {Object} an object whose attributes represent a participant.
     */
    self.participantForCurrentUser = ko.computed(function () {
        return _.findLast(self.participants(), function (participant) {
            const purpose = ko.unwrap(participant.purpose);
            return (
                participant.userId() === ownerId &&
                (purpose === "user" || purpose === "agent")
            );
        });
    });

    /**
     * Get an array of all HUMAN participants in a communication that are not the user that is
     * currently authenticated.
     *
     * @return {Array} an array of objects who's attributes represent a participant.
     */
    self.otherParticipants = ko.computed(function () {
        return _.reject(self.participants(), isCurrentUserOrMachineParticipant);
    });

    /**
     * Retrieve ALL of the other MACHINE participants.
     */
    self.otherMachineParticipants = ko.computed(function () {
        return _.filter(self.participants(), isMachineParticipant);
    });

    /**
     * Track connected HUMAN participants
     */
    self.connectedParticipants = ko.computed(function () {
        return _.reject(self.participants(), function (participant) {
            return (
                isMachineParticipant(participant) ||
                participant.isDisconnected() ||
                // TODO:  the public-api will eventually filter these out.  This can be removed when that is done.
                participant.monitoredParticipantId() !== null ||
                participant.coachedParticipantId() !== null ||
                participant.bargedParticipantId() !== null
            );
        });
    });

    /**
     * Track the current participants who are customers.
     * participant.purpose() will be one of: "manual, inbound, dialer, or customer"
     */
    self.customerParticipants = ko.computed(function () {
        return _.reject(self.otherParticipants(), function (participant) {
            return !_.includes(CUSTOMER_PARTICIPANTS, participant.purpose());
        });
    });

    ////////////////
    // Queue Helpers
    ////////////////

    self.queueName = ko.observable(); //TODO populate this somehow

    self.queueId = ko.computed(function () {
        if (
            self.participantForCurrentUser() &&
            self.participantForCurrentUser().queueId
        ) {
            return self.participantForCurrentUser().queueId();
        }

        return null;
    });

    /////////////////////
    // Communication State Helpers
    /////////////////////
    self.isConnecting = ko.computed(function () {
        if (!self.participantForCurrentUser()) {
            return false;
        }

        const status = self.participantForCurrentUser().status();
        const isConnecting =
            status === participantStatus.CONNECTING ||
            status === participantStatus.CONTACTING ||
            status === participantStatus.DIALING;
        if (isConnecting) {
            loggingService.debug("Connecting communication");
        }
        return isConnecting;
    });

    self.isHeld = ko.computed(function () {
        if (!self.participantForCurrentUser()) {
            return false;
        }

        return self.participantForCurrentUser().held();
    });

    /**
     * A helper method to determine if the user's communication is in a connected status.
     *
     * @return {Boolean} true if the communication is in a connected status, false otherwise.
     */
    self.isConnected = ko.computed(function () {
        if (!self.participantForCurrentUser()) {
            return false;
        }

        const status = self.participantForCurrentUser().status();
        const isConnected = status === participantStatus.CONNECTED;
        if (isConnected) {
            loggingService.debug("User Participant Connected");
        }
        return isConnected;
    });

    /**
     * A helper method to determine if the user's connection to a communication is in the
     * alerting phase.
     *
     * @return {Boolean} true if the communication is not yet connected, false otherwise.
     */
    self.isAlerting = ko.computed(function () {
        if (!self.participantForCurrentUser()) {
            return false;
        }

        const status = self.participantForCurrentUser().status();
        const isAlerting =
            status === participantStatus.ALERTING ||
            status === participantStatus.OFFERING;
        if (isAlerting) {
            loggingService.debug("Alerting User Participant.");
        }
        return isAlerting;
    });

    self.isDisconnected = ko.computed(function () {
        if (!self.participantForCurrentUser()) {
            return false;
        }

        return self.participantForCurrentUser().isDisconnected();
    });

    self.state = ko.computed(function () {
        if (self.isConnecting()) {
            return PARTICIPANT_STATES.CONNECTING;
        } else if (self.isHeld()) {
            return PARTICIPANT_STATES.HELD;
        } else if (self.isConnected()) {
            //and not held
            return PARTICIPANT_STATES.CONNECTED;
        } else if (self.isAlerting()) {
            return PARTICIPANT_STATES.ALERTING;
        } else if (self.isDisconnected()) {
            return PARTICIPANT_STATES.DISCONNECTED;
        }
        return PARTICIPANT_STATES.UNKNOWN;
    });
};

export default communicationMixin;
